<script setup lang="ts">
import type { AccordionItem } from '#ui/types';
import lodashValues from 'lodash-es/values';

import { AssetEuroNorm, AssetFuel, AssetType, BusType, SellingMethod } from '~/apiClient';
import { ROUTE_NAMES } from '~/constants/routes';
import { PlatformBusClass } from '~/types/asset.type';
import { AlpFilterableState, DslpFilterableState } from '~/types/filters';

// COMPOSABLES
const { tt, locale } = useTypedI18n();
const sellingMethod = useSlpSellingMethod();
const {
    // Filters
    busType,
    platformBusClass,
    euroNorm,
    fuel,
    aState,
    dsState,

    // Options
    requireScrappingOptions,
    brandsOptions,
    modelsOptions,
    versionsOptions,
    yearFromOptions,
    yearToOptions,
    kmFromOptions,
    kmToOptions,
    lengthFromOptions,
    lengthToOptions,
    priceFromOptions,
    priceToOptions,
    seatsFromOptions,
    seatsToOptions,

    ...slpParams
} = useSlpParams(sellingMethod);
const { formatSlpRoute } = useSlpRouteUtils();
const { onSlpFilterChangeByKey, onSlpFilterChangeByValue } = useTracking();

// DATA
const items = computed<AccordionItem[]>(() => [
    { label: tt(`slp.${sellingMethod.value}.filters.state.title`), slot: 'state' },
    { label: tt('slp.filters.price'), slot: 'price' },
    { label: tt('slp.filters.scrapping.title'), slot: 'scrapping' },
    { label: tt('slp.filters.busType'), slot: 'busType' },
    { label: tt('slp.filters.class'), slot: 'class' },
    { label: tt('slp.filters.brand'), slot: 'brand' },
    { label: tt('slp.filters.antiPollution'), slot: 'anti-pollution' },
    { label: tt('slp.filters.registration'), slot: 'registration' },
    { label: tt('slp.filters.kilometers'), slot: 'kilometers' },
    { label: tt('slp.filters.fuel'), slot: 'fuel' },
    { label: tt('slp.filters.length'), slot: 'length' },
    { label: tt('slp.filters.seats'), slot: 'seats' },
]);

const checkboxUi = { container: 'self-center', base: 'h-5 w-5', label: 'text-base' };

const requireScrapping = adaptEmptyOptionModel(slpParams.requireScrapping);
const yearFrom = adaptEmptyOptionModel(slpParams.yearFrom);
const yearTo = adaptEmptyOptionModel(slpParams.yearTo);
const kmFrom = adaptEmptyOptionModel(slpParams.kmFrom);
const kmTo = adaptEmptyOptionModel(slpParams.kmTo);
const brandId = adaptEmptyOptionModel(slpParams.brandId);
const brandName = adaptEmptyOptionModel(slpParams.brandName);
const modelId = adaptEmptyOptionModel(slpParams.modelId);
const versionId = adaptEmptyOptionModel(slpParams.versionId);
const lengthFrom = adaptEmptyOptionModel(slpParams.lengthFrom);
const lengthTo = adaptEmptyOptionModel(slpParams.lengthTo);
const seatsFrom = adaptEmptyOptionModel(slpParams.seatsFrom);
const seatsTo = adaptEmptyOptionModel(slpParams.seatsTo);
const priceFrom = adaptEmptyOptionModel(slpParams.priceFrom);
const priceTo = adaptEmptyOptionModel(slpParams.priceTo);

const requireScrappingSelectOptions = computed(() => prependEmptyOption(requireScrappingOptions.value, tt('slp.filters.scrapping.all')));
const yearFromSelectOptions = computed(() => prependEmptyOption(yearFromOptions.value, tt('slp.filters.yearAnyOption')));
const yearToSelectOptions = computed(() => prependEmptyOption(yearToOptions.value, tt('slp.filters.yearAnyOption')));
const kmFromSelectOptions = computed(() => prependEmptyOption(kmFromOptions.value, tt('slp.filters.kmAnyOption')));
const kmToSelectOptions = computed(() => prependEmptyOption(kmToOptions.value, tt('slp.filters.kmAnyOption')));
const brandsSelectOptions = computed(() => prependEmptyOption(brandsOptions.value, tt('slp.filters.brandFilterAnyOption')));
const modelsSelectOptions = computed(() => prependEmptyOption(modelsOptions.value, tt('slp.filters.modelFilterAnyOption')));
const versionsSelectOptions = computed(() => prependEmptyOption(versionsOptions.value, tt('slp.filters.versionFilterAnyOption')));
const lengthFromSelectOptions = computed(() => prependEmptyOption(lengthFromOptions.value, tt('slp.filters.lengthAnyOption')));
const lengthToSelectOptions = computed(() => prependEmptyOption(lengthToOptions.value, tt('slp.filters.lengthAnyOption')));
const seatsFromSelectOptions = computed(() => prependEmptyOption(seatsFromOptions.value, tt('slp.filters.seatsAnyOption')));
const seatsToSelectOptions = computed(() => prependEmptyOption(seatsToOptions.value, tt('slp.filters.seatsAnyOption')));
const priceFromSelectOptions = computed(() => prependEmptyOption(priceFromOptions.value, tt('slp.filters.priceAnyOption')));
const priceToSelectOptions = computed(() => prependEmptyOption(priceToOptions.value, tt('slp.filters.priceAnyOption')));
</script>

<template>
    <BaseAccordion multiple :items="items" default-open type="multiple">
        <template #state>
            <div class="pb-3.5">
                <template v-if="sellingMethod === SellingMethod.Auction">
                    <template v-for="_state in lodashValues(AlpFilterableState)" :key="_state">
                        <UCheckbox
                            v-model="aState[_state].value"
                            :label="tt(`slp.Auction.filters.state.${_state}`)"
                            :ui="checkboxUi"
                            @update:model-value="onSlpFilterChangeByValue(['state', _state], $event)"
                        />
                    </template>
                </template>
                <template v-if="sellingMethod === SellingMethod.DirectSale">
                    <template v-for="_state in lodashValues(DslpFilterableState)" :key="_state">
                        <UCheckbox
                            v-model="dsState[_state].value"
                            :label="tt(`slp.DirectSale.filters.state.${_state}`)"
                            :ui="checkboxUi"
                            @update:model-value="onSlpFilterChangeByValue(['state', _state], $event)"
                        />
                    </template>
                </template>
            </div>
        </template>
        <template #price>
            <div class="flex flex-col gap-3 pb-3.5 sm:flex-row sm:gap-8 lg:flex-col lg:gap-3 lg:pl-1">
                <UFormField :label="tt('slp.filters.priceFrom')" class="sm:grow">
                    <USelectMenu
                        v-model="priceFrom"
                        :search-input="false"
                        :items="priceFromSelectOptions"
                        value-key="value"
                        @update:model-value="onSlpFilterChangeByValue('price_from', $event)"
                    />
                </UFormField>
                <UFormField :label="tt('slp.filters.priceTo')" class="sm:grow">
                    <USelectMenu
                        v-model="priceTo"
                        :search-input="false"
                        :items="priceToSelectOptions"
                        value-key="value"
                        @update:model-value="onSlpFilterChangeByValue('price_to', $event)"
                    />
                </UFormField>
            </div>
        </template>
        <template #scrapping>
            <UFormField class="pb-3.5">
                <USelectMenu
                    v-model="requireScrapping"
                    :items="requireScrappingSelectOptions"
                    value-key="value"
                    :search-input="false"
                    @update:model-value="onSlpFilterChangeByValue('require_scrapping', $event)"
                />
                <template #help>
                    <NuxtLinkLocale :to="{ name: ROUTE_NAMES.FAQ, hash: '#scrapping' }" class="line-clamp-1 break-all underline" target="_blank">
                        {{ tt('slp.filters.scrapping.help') }}
                    </NuxtLinkLocale>
                </template>
            </UFormField>
        </template>
        <template #busType>
            <div class="flex flex-col gap-3 pb-3.5 lg:px-1">
                <UCheckbox
                    v-for="(_type, index) in Object.values(BusType)"
                    :key="index"
                    v-model="busType[_type].value"
                    :name="_type"
                    :label="tt(`asset.busType.${_type}`)"
                    :ui="checkboxUi"
                />
            </div>
        </template>
        <template #class>
            <div class="flex flex-col gap-3 pb-3.5 lg:px-1">
                <template v-for="(_class, index) in Object.values(PlatformBusClass)" :key="index">
                    <UCheckbox
                        v-model="platformBusClass[_class].value"
                        :name="_class"
                        :label="tt(`asset.class.${_class}`)"
                        :ui="checkboxUi"
                        @update:model-value="onSlpFilterChangeByValue(['class', _class], $event)"
                    />
                    <NuxtLinkLocale :to="formatSlpRoute({ sellingMethod, assetType: AssetType.Bus, platformClass: _class })" class="hidden">
                        {{ tt(`asset.class.${_class}`) }}
                    </NuxtLinkLocale>
                </template>
            </div>
        </template>
        <template #brand>
            <div class="flex flex-col gap-3 pb-3.5 sm:flex-row sm:gap-5 lg:flex-col lg:gap-3 lg:px-1">
                <UFormField :label="tt('slp.filters.brandFilter')" class="sm:grow">
                    <USelectMenu
                        v-model="brandId"
                        :disabled="brandsOptions.length === 0"
                        :items="brandsSelectOptions"
                        value-key="value"
                        @update:model-value="onSlpFilterChangeByKey('brand', brandsSelectOptions, $event)"
                    />
                    <template v-for="brand in brandsOptions" :key="brand.value">
                        <NuxtLinkLocale
                            :to="
                                formatSlpRoute({
                                    sellingMethod,
                                    assetType: AssetType.Bus,
                                    brandUrlKey: brand.urlKey,
                                })
                            "
                            class="hidden"
                        >
                            {{ brand.label }}
                        </NuxtLinkLocale>
                    </template>
                </UFormField>
                <UFormField :label="tt('slp.filters.modelFilter')" class="sm:grow">
                    <USelectMenu
                        v-model="modelId"
                        :disabled="!brandId || modelsOptions.length === 0"
                        :items="modelsSelectOptions"
                        value-key="value"
                        @update:model-value="onSlpFilterChangeByKey('model', modelsSelectOptions, $event)"
                    />
                    <template v-if="!!brandId">
                        <template v-for="model in modelsOptions" :key="model.value">
                            <NuxtLinkLocale
                                :to="
                                    formatSlpRoute({
                                        sellingMethod,
                                        assetType: AssetType.Bus,
                                        brandUrlKey: model.brandUrlKey,
                                        modelUrlKey: model.urlKey,
                                    })
                                "
                                class="hidden"
                            >
                                {{ brandName }} {{ model.label }}
                            </NuxtLinkLocale>
                        </template>
                    </template>
                </UFormField>
                <UFormField :label="tt('slp.filters.versionFilter')" class="sm:grow">
                    <USelectMenu
                        v-model="versionId"
                        :disabled="!modelId || versionsOptions.length === 0"
                        :items="versionsSelectOptions"
                        value-key="value"
                        @update:model-value="onSlpFilterChangeByKey('version', versionsSelectOptions, $event)"
                    />
                </UFormField>
            </div>
        </template>
        <template #anti-pollution>
            <div class="flex flex-col gap-3 pb-3.5 lg:px-1">
                <UCheckbox
                    v-for="(_euroNorm, index) in Object.values(AssetEuroNorm)"
                    :key="index"
                    v-model="euroNorm[_euroNorm].value"
                    :name="_euroNorm"
                    :label="tt(`asset.euroNorm.${_euroNorm}`)"
                    :ui="checkboxUi"
                    @update:model-value="onSlpFilterChangeByValue(['euro_norm', _euroNorm], $event)"
                />
            </div>
        </template>
        <template #registration>
            <div class="flex flex-col gap-3 pb-3.5 sm:flex-row sm:gap-8 lg:flex-col lg:gap-3 lg:px-1">
                <UFormField :label="tt('slp.filters.yearFrom')" class="sm:grow">
                    <USelectMenu
                        v-model="yearFrom"
                        value-key="value"
                        :items="yearFromSelectOptions"
                        :search-input="false"
                        @update:model-value="onSlpFilterChangeByValue('year_from', $event)"
                    />
                </UFormField>
                <UFormField :label="tt('slp.filters.yearTo')" class="sm:grow">
                    <USelectMenu
                        v-model="yearTo"
                        value-key="value"
                        :items="yearToSelectOptions"
                        :search-input="false"
                        @update:model-value="onSlpFilterChangeByValue('year_to', $event)"
                    />
                </UFormField>
            </div>
        </template>
        <template #kilometers>
            <div class="flex flex-col gap-3 pb-3.5 sm:flex-row sm:gap-8 lg:flex-col lg:gap-3 lg:pl-1">
                <UFormField :label="tt('slp.filters.kmFrom')" class="sm:grow">
                    <USelectMenu
                        v-model="kmFrom"
                        :items="kmFromSelectOptions"
                        :search-input="false"
                        value-key="value"
                        @update:model-value="onSlpFilterChangeByValue('km_from', $event)"
                    />
                </UFormField>
                <UFormField :label="tt('slp.filters.kmTo')" class="sm:grow">
                    <USelectMenu
                        v-model="kmTo"
                        :items="kmToSelectOptions"
                        value-key="value"
                        :search-input="false"
                        @update:model-value="onSlpFilterChangeByValue('km_to', $event)"
                    />
                </UFormField>
            </div>
        </template>
        <template #fuel>
            <div class="flex flex-col gap-3 pb-3.5 lg:px-1">
                <UCheckbox
                    v-for="(_fuel, index) in Object.values(AssetFuel)"
                    :key="index"
                    v-model="fuel[_fuel].value"
                    :name="_fuel"
                    :label="tt(`asset.fuel.${_fuel}`)"
                    :ui="checkboxUi"
                    @update:model-value="onSlpFilterChangeByValue(['fuel', _fuel], $event)"
                />
            </div>
        </template>
        <template #length>
            <div class="flex flex-col gap-3 pb-3.5 sm:flex-row sm:gap-8 lg:flex-col lg:gap-3 lg:px-1">
                <UFormField :label="tt('slp.filters.lengthFrom')" class="sm:grow">
                    <USelectMenu
                        :key="`${lengthTo}-${locale}`"
                        v-model="lengthFrom"
                        value-key="value"
                        :items="lengthFromSelectOptions"
                        @update:model-value="onSlpFilterChangeByValue('length_from', $event)"
                    />
                </UFormField>
                <UFormField :label="tt('slp.filters.lengthTo')" class="sm:grow">
                    <USelectMenu
                        :key="`${lengthFrom}-${locale}`"
                        v-model="lengthTo"
                        value-key="value"
                        :items="lengthToSelectOptions"
                        @update:model-value="onSlpFilterChangeByValue('length_to', $event)"
                    />
                </UFormField>
            </div>
        </template>
        <template #seats>
            <div class="flex flex-col gap-3 pb-3.5 sm:flex-row sm:gap-8 lg:flex-col lg:gap-3 lg:pl-1">
                <UFormField :label="tt('slp.filters.seatsFrom')" class="sm:grow">
                    <USelectMenu
                        v-model="seatsFrom"
                        :items="seatsFromSelectOptions"
                        :search-input="false"
                        value-key="value"
                        @update:model-value="onSlpFilterChangeByValue('seats_from', $event)"
                    />
                </UFormField>
                <UFormField :label="tt('slp.filters.seatsTo')" class="sm:grow">
                    <USelectMenu
                        v-model="seatsTo"
                        :items="seatsToSelectOptions"
                        :search-input="false"
                        value-key="value"
                        @update:model-value="onSlpFilterChangeByValue('seats_to', $event)"
                    />
                </UFormField>
            </div>
        </template>
    </BaseAccordion>
</template>
